export const normalizeReportTime = (time) => {
  if (!time) return "";
  const parts = String(time).trim().split(":");
  if (parts.length >= 3) {
    return `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}:${parts[2]
      .padStart(2, "0")
      .slice(0, 2)}`;
  }
  if (parts.length === 2) {
    return `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}:00`;
  }
  return String(time);
};

export const formatReportDateParts = (value) => {
  if (!value) return null;

  if (typeof value === "string" && value.includes("-") && value.includes(" ")) {
    const [datePart, ...timeParts] = value.trim().split(/\s+/);
    const timePart = timeParts.join(" ");
    if (datePart && timePart) {
      return { date: datePart, time: normalizeReportTime(timePart) };
    }
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");

  return {
    date: `${day}-${month}-${year}`,
    time: `${hours}:${minutes}:${seconds}`,
  };
};

export const formatReportDateTime = (createdAt) => {
  const parts = formatReportDateParts(createdAt);
  if (!parts) return "-";
  return `${parts.date} ${parts.time}`;
};

/** Canonical `issuer.type` / `issuer_type` filter values (PROLINE-3324). */
export const REPORT_ISSUER_TYPES = {
  employee: "Employee",
  supervisor: "Supervisor",
  yoramAdmin: "Yoram Admin",
};

const ISSUER_TYPE_ALIASES = {
  employee: REPORT_ISSUER_TYPES.employee,
  supervisor: REPORT_ISSUER_TYPES.supervisor,
  "yoram admin": REPORT_ISSUER_TYPES.yoramAdmin,
  yoram_admin: REPORT_ISSUER_TYPES.yoramAdmin,
  organizer: REPORT_ISSUER_TYPES.supervisor,
  admin: REPORT_ISSUER_TYPES.yoramAdmin,
  "مشرف": REPORT_ISSUER_TYPES.supervisor,
  "المشرف": REPORT_ISSUER_TYPES.supervisor,
  "موظف": REPORT_ISSUER_TYPES.employee,
  "الموظف": REPORT_ISSUER_TYPES.employee,
  "مسئول يُرام": REPORT_ISSUER_TYPES.yoramAdmin,
  "مسؤول يرام": REPORT_ISSUER_TYPES.yoramAdmin,
};

/** Normalize legacy labels (`Organizer` / `Admin`) to the canonical set. */
export const normalizeIssuerType = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const aliased = ISSUER_TYPE_ALIASES[raw.toLowerCase()];
  if (aliased) return aliased;
  if (
    raw === REPORT_ISSUER_TYPES.employee ||
    raw === REPORT_ISSUER_TYPES.supervisor ||
    raw === REPORT_ISSUER_TYPES.yoramAdmin
  ) {
    return raw;
  }
  return raw;
};

/** Filter options for Reporter Position (`issuer_type` query param). */
export const getReporterPositionFilterOptions = (labels = {}) => [
  {
    id: REPORT_ISSUER_TYPES.supervisor,
    name: labels.supervisor || REPORT_ISSUER_TYPES.supervisor,
  },
  {
    id: REPORT_ISSUER_TYPES.employee,
    name: labels.employee || REPORT_ISSUER_TYPES.employee,
  },
  {
    id: REPORT_ISSUER_TYPES.yoramAdmin,
    name: labels.yoramAdmin || REPORT_ISSUER_TYPES.yoramAdmin,
  },
];

/** Build list query params for logistics issues (admin + company). */
export const buildCompanyReportsListParams = ({
  search,
  page,
  pageSize,
  eventId,
  zoneId,
  status,
  issuerType,
  reportedAt,
  resolvedAt,
}) => {
  const params = { page, items: pageSize };
  const term = (search || "").trim();
  if (term) {
    params.title = term;
  }
  if (eventId) params.event_id = eventId;
  if (zoneId) params.zone_id = zoneId;
  if (issuerType) params.issuer_type = normalizeIssuerType(issuerType);
  if (reportedAt?.startDate) {
    params.created_at_start_date = reportedAt.startDate;
  }
  if (reportedAt?.endDate) {
    params.created_at_end_date = reportedAt.endDate;
  }
  if (resolvedAt?.startDate) {
    params.closed_at_start_date = resolvedAt.startDate;
  }
  if (resolvedAt?.endDate) {
    params.closed_at_end_date = resolvedAt.endDate;
  }
  if (Array.isArray(status) && status.length) {
    params.status = status.join(",");
  } else if (typeof status === "string" && status) {
    params.status = status;
  }
  return params;
};

/** PROLINE-3321 — API `columns` keys (order = sheet column order). */
export const REPORT_EXPORT_COLUMN_KEYS = [
  "event_name",
  "title",
  "description",
  "pictures_url",
  "date",
  "time",
  "reporter_name",
  "reporter_position",
  "status",
  "resolution_date",
  "resolution_time",
  "zone_name",
  "zone_supervisor",
  "comments",
];

export const EMPTY_REPORT_EXPORT_FIELDS = REPORT_EXPORT_COLUMN_KEYS.reduce(
  (acc, key) => {
    acc[key] = false;
    return acc;
  },
  {},
);

/** Modal field defs from `report.exportSheetColumns` (+ `updates` for comments). */
export const getReportExportFields = (exportLabels = {}, commentsLabel) => [
  { name: "event_name", label: exportLabels.eventName },
  { name: "title", label: exportLabels.reportName },
  { name: "description", label: exportLabels.description },
  { name: "pictures_url", label: exportLabels.attachments },
  { name: "date", label: exportLabels.reportedDate },
  { name: "time", label: exportLabels.reportedTime },
  { name: "reporter_name", label: exportLabels.reporterName },
  { name: "reporter_position", label: exportLabels.reporterPosition },
  { name: "status", label: exportLabels.status },
  { name: "resolution_date", label: exportLabels.resolutionDate },
  { name: "resolution_time", label: exportLabels.resolutionTime },
  { name: "zone_name", label: exportLabels.zone },
  { name: "zone_supervisor", label: exportLabels.zoneSupervisor },
  { name: "comments", label: commentsLabel || exportLabels.comments },
];

/**
 * Export query params: same filters as the list, `page=-1` for the full filtered set.
 * Company portal must include `event_id` in params (not a path segment).
 */
export const buildReportExportParams = ({
  columns,
  listParams = {},
  includeEventId = false,
}) => {
  const params = { ...listParams, columns, page: -1 };
  delete params.items;
  if (!includeEventId) {
    delete params.event_id;
  }
  return params;
};

export const joinSelectedReportExportColumns = (fields, selectedFields) =>
  fields
    .filter((field) => selectedFields[field.name])
    .map((field) => field.name)
    .join(",");

/** Canonical issuer type → `report.reporterPositions` key. */
const CANONICAL_ISSUER_POSITION_KEY = {
  [REPORT_ISSUER_TYPES.employee]: "employee",
  [REPORT_ISSUER_TYPES.supervisor]: "supervisor",
  [REPORT_ISSUER_TYPES.yoramAdmin]: "yoramAdmin",
};

/**
 * Localized role label from a logistics-issue issuer payload (`issuer.type` / role name).
 * The API `issuer.type` is already localized, but we normalize it to a canonical value
 * for consistency, so the display label must be re-localized from `reporterPositions`.
 */
export const resolveIssuerRole = (issuer, reporterPositions = {}) => {
  const role = issuer?.role_name || issuer?.role || issuer?.type || "";
  const canonical = normalizeIssuerType(role);
  if (!canonical) return "-";
  return reporterPositions[CANONICAL_ISSUER_POSITION_KEY[canonical]] || canonical;
};

export const mapReportRow = (issue, reporterPositions) => ({
  id: issue?.id,
  title: issue?.title || "-",
  eventId: issue?.event?.id || issue?.event_id || null,
  eventName: issue?.event?.name || "-",
  zoneName: issue?.zone?.name || "-",
  issuerName: issue?.issuer?.name || "-",
  issuerRole: resolveIssuerRole(issue?.issuer, reporterPositions),
  dateTime: formatReportDateTime(issue?.created_at),
  closedDateTime: formatReportDateTime(issue?.closed_at),
  status: String(issue?.status || "").toLowerCase(),
  raw: issue,
});
